import './loginScreen.scss'
import React, {useCallback, useEffect} from 'react';
import * as Yup from "yup";
import {CommonService} from "../../../shared/services";
import {useDispatch, useSelector} from "react-redux";
import {Field, FieldProps, Form, Formik, FormikHelpers} from 'formik';
import {setLoggedInUserData, setLoggedInUserToken, setUserSignature} from "../../../store/actions/account.action";
import {IAPIResponseType} from "../../../shared/models/api.model";
import {ILoginResponse} from "../../../shared/models/account.model";
import FormikInputComponent from "../../../shared/components/core/formik-input/FormikInputComponent";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import FormikPasswordInputComponent
    from "../../../shared/components/core/formik-password-input/FormikPasswordInputComponent";
import {useLocation, useNavigate} from "react-router-dom";
import commonService from "../../../shared/services/common.service";
import {Skeleton} from "@mui/material";
import {IRootReducerState} from "../../../store/reducers";
import {setSelectedLab} from "../../../store/actions/selectedLab.action";

const validationSchema = Yup.object({
    emailId: Yup.string()
        .email("Invalid Email ID")
        .matches(/^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/, "Enter a valid Email ID")
        .required("Email ID is required"),
    password: Yup.string()
        .min(8, "Password must contain at least 8 characters")
        .required("Password is required")
});

const initialValues = {
    emailId: "",
    password: "",
}

const LoginScreen = () => {
        const {systemConfig} = useSelector((state: IRootReducerState) => state?.staticData);
        const structuredConfig = CommonService.getStructuredSystemConfig(systemConfig?.data)

        const navigate = useNavigate();
        const dispatch = useDispatch();
        const location = useLocation();

        const verifyUserSignature = useCallback((userId: string) => {
            CommonService._account.VerifySignature(userId).then((response: IAPIResponseType<any>) => {
                dispatch(setUserSignature(response.data));
            }).catch(() => {
                dispatch(setUserSignature(null));
            })
        }, [dispatch])

        const onSubmit = useCallback((values: typeof initialValues, {
            setSubmitting,
            setErrors
        }: FormikHelpers<typeof initialValues>) => {
            setSubmitting(true);
            const payload: typeof initialValues = {
                ...values,
                emailId: values?.emailId?.toLowerCase()
            }
            CommonService._account.LoginAPICall(payload)
                .then((response: IAPIResponseType<ILoginResponse>) => {
                    dispatch(setLoggedInUserData(response.data))
                    dispatch(setLoggedInUserToken(response.data.token))
                    verifyUserSignature(response.data?.id)
                    if (Object.keys(response?.data?.labDetails).length) {
                        dispatch(setSelectedLab(response?.data?.labDetails?.id))
                    }
                    setSubmitting(false);
                })
                .catch((error: any) => {
                    CommonService.handleErrors(setErrors, error);
                    setSubmitting(false);
                })
        }, [dispatch, verifyUserSignature]);

        const handleNavigation = useCallback((route: string) => {
            let returnUrl = CommonService._routeConfig.DashboardRoute();
            const query = CommonService.parseQueryString(location.search);
            if (Object.keys(query).includes('returnUrl')) {
                returnUrl = query.returnUrl;
            }
            navigate(route + `?returnUrl=${returnUrl}`);
        }, [location, navigate]);

        return (
            <div className="login-screen screen width-100">
                {!systemConfig?.isLoading ? (
                    <>
                        <div className="logo-wrapper">
                            <img src={structuredConfig?.expandLogo} alt={'AdvanzInnovationLogo'}/>
                        </div>
                        <div className="login-welcome-text">
                            Welcome!
                        </div>
                        <div className="login-title auth-form-title">
                            Log in to your account
                        </div>
                        <div className="login-form">
                            <Formik
                                validationSchema={validationSchema}
                                initialValues={initialValues}
                                validateOnChange={false}
                                validateOnBlur={true}
                                enableReinitialize={true}
                                validateOnMount={true}
                                onSubmit={onSubmit}
                            >
                                {({isSubmitting, values, isValid, validateForm}) => {
                                    // eslint-disable-next-line react-hooks/rules-of-hooks
                                    useEffect(() => {
                                        validateForm();
                                    }, [validateForm, values]);
                                    return (
                                        <Form className={"login-holder"} noValidate={true}>
                                            <Field name={'emailId'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Email ID'}
                                                            placeholder={'Enter Email ID'}
                                                            type={"email"}
                                                            id={"email_input"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'password'} className="t-form-control">
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikPasswordInputComponent
                                                            label={'Password'}
                                                            placeholder={'Enter Password'}
                                                            required={true}
                                                            formikField={field}
                                                            canToggle={true}
                                                            fullWidth={true}
                                                            id={"password_input"}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <div className="forgot-password" onClick={() => {
                                                handleNavigation(commonService._routeConfig.ForgotPasswordRoute())
                                            }}>
                                                Forgot Password?
                                            </div>
                                            <ButtonComponent
                                                type={"submit"}
                                                color={"primary"}
                                                variant={"contained"}
                                                fullWidth={true}
                                                disabled={isSubmitting}
                                                isLoading={isSubmitting}
                                                id={"login_btn"}
                                                buttonProps={{
                                                    id: "login_btn"
                                                }}
                                            >
                                                {isSubmitting ? "Logging In" : "Login"}
                                            </ButtonComponent>
                                        </Form>
                                    )
                                }}
                            </Formik>
                        </div>
                        {/*<div className="branding-wrapper">*/}
                        {/*    <div className={"branding-prefix"}>Powered By</div>*/}
                        {/*    <div className="branding-logo">*/}
                        {/*        <img src={ImageConfig.BrandingImage} alt="branding"/>*/}
                        {/*    </div>*/}
                        {/*</div>*/}
                    </>) : <>
                    <Skeleton animation="wave" className={"logo-wrapper logo-wrapper-skeleton"} variant={'rectangular'}
                              width={195}
                              height={45}/>
                    <Skeleton animation="wave" className={"login-welcome-text"} variant={'text'} width={83}
                              height={24}/>
                    <Skeleton animation="wave" className={"login-title auth-form-title"} variant={'text'} width={269}
                              height={32}/>
                    <div className="login-form">
                        <div className="login-holder">
                            <Skeleton animation="wave" className={"input-component-wrapper"} variant={'rectangular'}
                                      width={"100%"}
                                      height={50}/>
                            <Skeleton animation="wave" className={"input-component-wrapper"} variant={'rectangular'}
                                      width={"100%"}
                                      height={50}/>
                            <Skeleton animation="wave" className={"forgot-password forgot-password-skeleton"}
                                      variant={'rectangular'} width={109}
                                      height={18}/>
                            <Skeleton animation="wave" className={"login-button"} variant={'rectangular'} width={"100%"}
                                      height={36.5}/>
                            {/*<Skeleton animation="wave" className={"branding-wrapper branding-wrapper-skeleton"}*/}
                            {/*          variant={'rectangular'}*/}
                            {/*          width={"70%"}*/}
                            {/*          height={25}/>*/}
                        </div>
                    </div>
                </>}
            </div>
        )
    }

;

export default LoginScreen;
